import { ArrowDown, ArrowUp, ChevronsUpDown, Search } from 'lucide-react'
import { useMemo, useState, type ReactNode } from 'react'

import { QueryState } from '@/components/QueryState'
import { Button } from '@/components/ui/Button'
import { Checkbox } from '@/components/ui/Checkbox'
import { Input } from '@/components/ui/Input'
import { Skeleton } from '@/components/ui/skeleton'
import { cn } from '@/lib/cn'

/**
 * The table every list screen is built from: sorting, a search box, pagination and bulk
 * selection, with the loading / empty / forbidden states handled in the same place.
 *
 * Sorting and searching run over the rows that are currently loaded, not the whole result
 * set. That is honest for a page of 20 and wrong for a page of 20 out of 5,000 — so both
 * are wired as props rather than hidden inside, and the day the API grows `sort` and `q`
 * parameters this becomes a controlled component instead of a rewrite of every caller.
 */

export interface Column<T> {
  key: string
  header: string
  cell: (row: T) => ReactNode
  /** Supplying this makes the column sortable. Return null for "no value". */
  sortValue?: (row: T) => string | number | boolean | null | undefined
  className?: string
  headerClassName?: string
}

export interface DataTableProps<T> {
  rows: T[]
  columns: Column<T>[]
  getRowId: (row: T) => string
  /** Describes the table for screen readers; visually hidden. */
  caption: string
  isLoading?: boolean
  error?: unknown
  emptyMessage?: string
  /** Shown above the table, next to the search box — filters, a "New" button, and so on. */
  toolbar?: ReactNode
  search?: {
    placeholder?: string
    /** Return whether a row matches the typed query. The query is already lower-cased. */
    matches: (row: T, query: string) => boolean
  }
  selection?: {
    selected: string[]
    onChange: (ids: string[]) => void
    /** Rendered in a bar above the table while at least one row is selected. */
    actions?: (ids: string[]) => ReactNode
  }
  /**
   * Server-driven paging: the rows handed in are one page, and the buttons ask the caller
   * for another. `total` is only for the footer's row count — omit it and the footer just
   * counts pages.
   */
  pagination?: {
    page: number
    totalPages: number
    onPage: (page: number) => void
    isFetching?: boolean
    total?: number
  }
  /**
   * Client-side paging, for endpoints that answer with the whole set in one response —
   * a stock valuation, a chart of accounts. Rows are still all in memory; this stops the
   * browser laying out eight hundred of them at once and gives the reader a place to be.
   *
   * Ignored when `pagination` is set: paging a page is nonsense, and the server's version
   * is the honest one.
   */
  pageSize?: number
}

type SortState = { key: string; direction: 'asc' | 'desc' } | null

const isBlank = (value: unknown) => value === null || value === undefined || value === ''

function compare(a: unknown, b: unknown): number {
  if (a === b) return 0
  if (typeof a === 'number' && typeof b === 'number') return a - b
  return String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: 'base' })
}

/**
 * Blanks last in both directions -- which is why they are settled here rather than inside
 * `compare`, where reversing the result for a descending sort would reverse this too. A row
 * with no last-sign-in is not "earliest"; it is a row with nothing to compare, and floating
 * it to the top of the reversed list buries the rows the user actually asked to see.
 */
function compareWithBlanksLast(a: unknown, b: unknown, direction: 'asc' | 'desc'): number {
  if (isBlank(a) || isBlank(b)) {
    if (isBlank(a) && isBlank(b)) return 0
    return isBlank(a) ? 1 : -1
  }
  const result = compare(a, b)
  return direction === 'asc' ? result : -result
}

export function TableSkeleton({ columns, rows = 5 }: { columns: number; rows?: number }) {
  return (
    <div className="space-y-3 p-5" aria-busy="true" aria-label="Loading">
      {Array.from({ length: rows }, (_, rowIndex) => (
        <div key={rowIndex} className="flex gap-4">
          {Array.from({ length: columns }, (_, columnIndex) => (
            <Skeleton
              key={columnIndex}
              // The first column is a name in every table so far, so it gets the wider bar.
              // A skeleton that does not resemble the content it replaces is just a spinner
              // that takes up more room.
              className={cn('h-4', columnIndex === 0 ? 'w-40' : 'w-24')}
            />
          ))}
        </div>
      ))}
    </div>
  )
}

export function DataTable<T>({
  rows,
  columns,
  getRowId,
  caption,
  isLoading = false,
  error,
  emptyMessage = 'Nothing to show yet.',
  toolbar,
  search,
  selection,
  pagination,
  pageSize,
}: DataTableProps<T>) {
  const [query, setQuery] = useState('')
  const [sort, setSort] = useState<SortState>(null)
  const [clientPage, setClientPage] = useState(1)

  const visible = useMemo(() => {
    const trimmed = query.trim().toLowerCase()
    const filtered = search && trimmed ? rows.filter((row) => search.matches(row, trimmed)) : rows

    if (!sort) return filtered
    const column = columns.find((c) => c.key === sort.key)
    if (!column?.sortValue) return filtered

    const sortValue = column.sortValue
    // Copied before sorting: `rows` belongs to the query cache, and sorting it in place
    // would mutate cached data that React has no reason to think changed.
    return [...filtered].sort((a, b) =>
      compareWithBlanksLast(sortValue(a), sortValue(b), sort.direction),
    )
  }, [rows, columns, query, search, sort])

  // Paging runs after filtering and sorting, which is the only order a reader would guess:
  // search first, then look through what matched a screenful at a time.
  const clientSize = pagination ? undefined : pageSize
  const clientPages = clientSize ? Math.max(1, Math.ceil(visible.length / clientSize)) : 1
  // Rows can vanish from under the current page -- type in the search box on page 7 and
  // there may be one page left. Clamping here rather than resetting in an effect means the
  // first paint is already right instead of being corrected on a second one.
  const page = Math.min(clientPage, clientPages)
  const start = clientSize ? (page - 1) * clientSize : 0
  const paged = clientSize ? visible.slice(start, start + clientSize) : visible

  const visibleIds = paged.map(getRowId)
  const selected = selection ? new Set(selection.selected) : null
  const selectedHere = visibleIds.filter((id) => selected?.has(id))
  const allSelected = visibleIds.length > 0 && selectedHere.length === visibleIds.length

  const toggleAll = () => {
    if (!selection) return
    // Only the rows in view are affected: a "select all" that silently included rows on
    // pages the user has never looked at would be a genuinely dangerous button.
    const others = selection.selected.filter((id) => !visibleIds.includes(id))
    selection.onChange(allSelected ? others : [...others, ...visibleIds])
  }

  const toggleRow = (id: string) => {
    if (!selection) return
    selection.onChange(
      selected?.has(id)
        ? selection.selected.filter((other) => other !== id)
        : [...selection.selected, id],
    )
  }

  const toggleSort = (key: string) => {
    setSort((current) =>
      current?.key === key
        ? current.direction === 'asc'
          ? { key, direction: 'desc' }
          : null // third click clears it and restores the server's order
        : { key, direction: 'asc' },
    )
  }

  const columnCount = columns.length + (selection ? 1 : 0)

  /**
   * One footer for both kinds of paging. It is drawn even at a single page: a table that
   * says how many rows it is showing has answered the question "is this all of them?",
   * and a table that says nothing leaves the reader to count.
   */
  const pager = pagination
    ? {
        page: pagination.page,
        totalPages: pagination.totalPages,
        onPage: pagination.onPage,
        isFetching: pagination.isFetching ?? false,
        label:
          pagination.total === undefined
            ? `Page ${pagination.page} of ${pagination.totalPages}`
            : `Page ${pagination.page} of ${pagination.totalPages} · ${pagination.total} rows`,
      }
    : clientSize
      ? {
          page,
          totalPages: clientPages,
          onPage: setClientPage,
          isFetching: false,
          label: `${start + 1}–${start + paged.length} of ${visible.length}`,
        }
      : null

  return (
    <div>
      {(search || toolbar) && (
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-200 px-5 py-3 dark:border-slate-800">
          {search ? (
            <div className="relative min-w-0 flex-1 sm:max-w-xs">
              <Search
                className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400"
                aria-hidden="true"
              />
              <Input
                type="search"
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder={search.placeholder ?? 'Search…'}
                aria-label={`Search ${caption.toLowerCase()}`}
                className="pl-9"
              />
            </div>
          ) : (
            <div />
          )}
          {toolbar && <div className="flex items-center gap-2">{toolbar}</div>}
        </div>
      )}

      {selection && selectedHere.length > 0 && (
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-200 bg-brand-50 px-5 py-2 text-sm dark:border-slate-800 dark:bg-brand-900/30">
          <span>
            {selectedHere.length} selected
            <Button
              variant="ghost"
              size="sm"
              className="ml-1"
              onClick={() => selection.onChange([])}
            >
              Clear
            </Button>
          </span>
          {selection.actions && (
            <div className="flex items-center gap-2">{selection.actions(selection.selected)}</div>
          )}
        </div>
      )}

      <QueryState
        isLoading={isLoading}
        error={error}
        isEmpty={visible.length === 0}
        emptyMessage={
          query.trim() && rows.length > 0 ? `Nothing matches “${query.trim()}”.` : emptyMessage
        }
        skeleton={<TableSkeleton columns={columnCount} />}
      >
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm">
            <caption className="sr-only">{caption}</caption>
            <thead className="border-b border-slate-200 text-xs uppercase tracking-wide text-slate-500 dark:border-slate-800">
              <tr>
                {selection && (
                  <th scope="col" className="w-10 px-5 py-3">
                    <Checkbox
                      checked={allSelected}
                      indeterminate={selectedHere.length > 0 && !allSelected}
                      onChange={toggleAll}
                      aria-label={allSelected ? 'Clear selection' : 'Select all rows on this page'}
                    />
                  </th>
                )}
                {columns.map((column) => {
                  const active = sort?.key === column.key
                  const SortIcon = !active
                    ? ChevronsUpDown
                    : sort.direction === 'asc'
                      ? ArrowUp
                      : ArrowDown
                  return (
                    <th
                      key={column.key}
                      scope="col"
                      className={cn('px-5 py-3 font-medium', column.headerClassName)}
                      aria-sort={
                        active ? (sort.direction === 'asc' ? 'ascending' : 'descending') : undefined
                      }
                    >
                      {column.sortValue ? (
                        <button
                          type="button"
                          onClick={() => toggleSort(column.key)}
                          className="inline-flex items-center gap-1 uppercase tracking-wide hover:text-slate-900 dark:hover:text-slate-100"
                        >
                          {column.header}
                          <SortIcon
                            className={cn('h-3 w-3', active ? 'opacity-100' : 'opacity-40')}
                            aria-hidden="true"
                          />
                        </button>
                      ) : (
                        column.header
                      )}
                    </th>
                  )
                })}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
              {paged.map((row) => {
                const id = getRowId(row)
                const isSelected = selected?.has(id) ?? false
                return (
                  <tr
                    key={id}
                    className={cn(
                      'hover:bg-slate-50 dark:hover:bg-slate-800/50',
                      isSelected && 'bg-brand-50/60 dark:bg-brand-900/20',
                    )}
                  >
                    {selection && (
                      <td className="px-5 py-3">
                        <Checkbox
                          checked={isSelected}
                          onChange={() => toggleRow(id)}
                          aria-label={`Select row ${id}`}
                        />
                      </td>
                    )}
                    {columns.map((column) => (
                      <td key={column.key} className={cn('px-5 py-3', column.className)}>
                        {column.cell(row)}
                      </td>
                    ))}
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>

        {pager && (
          <div className="flex items-center justify-between border-t border-slate-200 px-5 py-3 text-sm dark:border-slate-800">
            <span className="text-slate-500 dark:text-slate-400">{pager.label}</span>
            <div className="flex gap-2">
              <Button
                variant="secondary"
                size="sm"
                disabled={pager.page <= 1 || pager.isFetching}
                onClick={() => pager.onPage(pager.page - 1)}
              >
                Previous
              </Button>
              <Button
                variant="secondary"
                size="sm"
                disabled={pager.page >= pager.totalPages || pager.isFetching}
                onClick={() => pager.onPage(pager.page + 1)}
              >
                Next
              </Button>
            </div>
          </div>
        )}
      </QueryState>
    </div>
  )
}
